// src/components/Dashboard/widgets/ProgressClaimsWidget.tsx
// Widget 6 — Progress Claims Status
// Click any status count → Claims module at that status filter.
// Click eligible value card → Claims module ready-to-submit view.
import { DollarSign, ChevronRight, CheckCircle, Clock, TrendingUp } from 'lucide-react'
import { getLiveMilestones, MOCK_MILESTONES } from '../../../utils/mockDataClaims'
import { formatCurrency } from '../../../utils/formatters'
import { WidgetProps } from '../types'
import { useDeepLinkNavigation } from '../../../hooks/useDeepLinkNavigation'

export function ProgressClaimsWidget({ onNavigate, scopedJobIds }: WidgetProps) {
  const { navigate } = useDeepLinkNavigation('dashboard', onNavigate)

  const all      = getLiveMilestones()
  const scoped   = scopedJobIds ? all.filter(m => scopedJobIds.includes(m.jobId)) : all

  const eligible  = scoped.filter(m => m.status === 'ELIGIBLE')
  const submitted = scoped.filter(m => m.status === 'SUBMITTED')
  const approved  = scoped.filter(m => (m.status as string) === 'APPROVED')
  const paid      = scoped.filter(m => m.status === 'PAID')

  const eligValue  = eligible.reduce( (s, m) => s + ((m as any).value ?? 0), 0)
  const subValue   = submitted.reduce((s, m) => s + ((m as any).value ?? 0), 0)
  const appValue   = approved.reduce( (s, m) => s + ((m as any).value ?? 0), 0)

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
      {/* Header */}
      <div
        className="px-5 py-3.5 border-b flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => navigate('claims')}
      >
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-green-600" />
          <span className="font-semibold text-gray-900 text-sm">Progress claims</span>
          {eligible.length > 0 && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
              {eligible.length} eligible
            </span>
          )}
        </div>
        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-4 divide-x border-b bg-gray-50/60">
        {[
          { label: 'Eligible',  count: eligible.length,  cls: 'text-green-600'  },
          { label: 'Submitted', count: submitted.length, cls: 'text-blue-600'   },
          { label: 'Approved',  count: approved.length,  cls: 'text-purple-600' },
          { label: 'Paid',      count: paid.length,       cls: 'text-gray-400'   },
        ].map(s => (
          <div
            key={s.label}
            className="px-2 py-2.5 text-center cursor-pointer hover:bg-white transition-colors"
            onClick={() => navigate('claims')}
          >
            <div className={`text-xl font-bold ${s.cls}`}>{s.count}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Value cards */}
      <div className="flex-1 px-5 py-4 space-y-2.5">
        {eligValue > 0 && (
          <div
            className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
            onClick={() => navigate('claims', { openTab: 'eligible' })}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <div>
                <p className="text-xs font-semibold text-green-800">Ready to submit</p>
                <p className="text-xs text-green-600">{eligible.length} milestone{eligible.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            <span className="text-base font-bold text-green-700">{formatCurrency(eligValue)}</span>
          </div>
        )}

        {subValue > 0 && (
          <div
            className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
            onClick={() => navigate('claims', { openTab: 'submitted' })}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-600" />
              <div>
                <p className="text-xs font-semibold text-blue-800">Awaiting assessment</p>
                <p className="text-xs text-blue-600">{submitted.length} submitted</p>
              </div>
            </div>
            <span className="text-base font-bold text-blue-700">{formatCurrency(subValue)}</span>
          </div>
        )}

        {appValue > 0 && (
          <div
            className="flex items-center justify-between p-3 bg-purple-50 rounded-xl border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
            onClick={() => navigate('claims', { openTab: 'approved' })}
          >
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <div>
                <p className="text-xs font-semibold text-purple-800">Approved — awaiting payment</p>
                <p className="text-xs text-purple-600">{approved.length} approved</p>
              </div>
            </div>
            <span className="text-base font-bold text-purple-700">{formatCurrency(appValue)}</span>
          </div>
        )}

        {eligible.length === 0 && submitted.length === 0 && approved.length === 0 && (
          <div className="py-4 text-center">
            <p className="text-sm text-gray-400">No claims requiring action</p>
          </div>
        )}
      </div>

      <div className="px-5 py-2.5 border-t">
        <button onClick={() => navigate('claims')} className="text-xs text-blue-600 hover:underline">
          Manage all claims →
        </button>
      </div>
    </div>
  )
}
